import { apiUrl, httpOptions } from './dataProvider'
import { unzipSync } from 'fflate'

export const GET = 'GET'
export const PUT = 'PUT'

export class HttpError extends Error {
  public readonly status: number
  public readonly data: unknown
  public readonly url: string
  public readonly statusText: string

  constructor(response: Response, url: string, data: Record<string, unknown>) {
    const statusText = response.statusText ? `: ${response.statusText}` : ''
    super(`Error status ${response.status}${statusText} retrieving: ${url}`)
    this.status = response.status
    this.data = data
    this.url = url
    this.statusText = response.statusText
  }
}

function apiRequest<T>(
  url: string,
  method: string,
  optionOverrides: Record<string, unknown> = {},
): Promise<T> {
  const options = {
    ...httpOptions(),
    ...optionOverrides,
  }
  return makeRequest2<T>(`${apiUrl()}${url}`, method, options)
}

function retryPromise<T>(
  promise: () => Promise<T>,
  options?: {
    retryIf?: (resp: T) => boolean
    retryCatchIf?: (e: unknown) => boolean
    retries?: number
  },
) {
  const { retryIf, retryCatchIf, retries } = {
    retryIf: () => false,
    retryCatchIf: () => true,
    retries: 1,
    ...options,
  }
  let _promise = promise()

  for (let i = 1; i < retries; i++)
    _promise = _promise
      .catch((value) =>
        retryCatchIf(value) ? promise() : Promise.reject(value),
      )
      .then((value) => (retryIf(value) ? promise() : Promise.reject(value)))

  return _promise
}

async function makeRequest2<T>(
  url: string,
  method: string,
  optionOverrides: RequestInit = {},
): Promise<T> {
  const options = {
    method,
    ...optionOverrides,
  }
  const retryIf = (resp: Response) => resp.status === 429
  const response = await retryPromise(() => fetch(url, options), { retryIf })
  switch (response.status) {
    case 204:
      return undefined as any
    case 200:
      switch (response.headers.get('Content-Type')) {
        case 'text/html':
        case 'text/csv':
          return (await response.text()) as any
        case 'application/zip': {
          const decompressed = unzipSync(
            new Uint8Array(await response.arrayBuffer()),
          )
          const contentKey = url.split('/')!.pop()!.replace('.zip', '')
          const content = decompressed[contentKey]
          const asText = new TextDecoder('utf-8').decode(content)
          if (contentKey.includes('.json')) {
            return JSON.parse(asText)
          }
          return asText as any
        }
        default:
          return response.json()
      }
  }
  let data
  try {
    data = await response.json()
  } catch (_) {
    data = null
  }
  throw new HttpError(response, url, data)
}

export async function makeRequestWithErrorReporting<T>({
  url,
  method,
  optionOverrides = {},
}: {
  url: string
  method: string
  optionOverrides?: RequestInit
}) {
  try {
    const result = await makeRequest2<T>(url, method, optionOverrides)
    return {
      result,
      error: undefined,
    }
  } catch (e) {
    return {
      result: undefined,
      error: `Request: ${e}`,
      status: (e as HttpError).status,
      data: (e as HttpError).data,
    }
  }
}

export function makeGetRequest<T>({
  url,
  optionOverrides,
}: {
  url: string
  optionOverrides?: RequestInit
}) {
  return makeRequestWithErrorReporting<T>({ url, method: GET, optionOverrides })
}

export function getAPIRequest<T>(path: string) {
  return apiRequest<T>(path, GET)
}

export async function fetchWithExponentialBackoff(
  url: string,
  options: RequestInit = {},
  shouldRetryOnError: (error: unknown) => boolean,
  maxRetries: number = 10,
  initialDelay: number = 1_000,
  multiplier: number = 2,
): Promise<string> {
  let retries = 0
  let delayTime = initialDelay

  while (true) {
    try {
      const response = await fetch(url, options)
      const data = await response.text()
      if (data.includes('User rate limit exceeded.')) {
        throw new Error('User rate limit exceeded.')
      }
      if (!response.ok) {
        throw new Error(response.statusText)
      }

      // If the fetch is successful, return the response
      return data
    } catch (error) {
      if (retries >= maxRetries || !shouldRetryOnError(error)) {
        throw error // Rethrow the error if max retries exceeded or should not retry
      }
      console.warn(
        `Attempt ${retries + 1} failed. Retrying in ${delayTime / 1000} seconds...`,
      )
      await new Promise((resolve) => setTimeout(resolve, delayTime)) // Wait for the delay
      delayTime *= multiplier // Exponential backoff
      retries += 1
    }
  }
}

export async function fetchZipfile(url: string) {
  const options = {
    ...httpOptions(),
    headers: {
      Accept: 'application/zip',
    },
  }
  fetch(`${apiUrl()}${url}`, options)
    .then((res) => res.blob())
    .then((blob) => {
      const file = window.URL.createObjectURL(blob)
      window.location.assign(file)
    })
}
